import type { AxiosInstance } from "axios";
import HttpFactory from "~/repository/factory";
import { ENDPOINTS } from "~/repository/endpoints";
import type {
  SchoolFinanceAction,
  SchoolFinanceCatalog,
  SchoolFinanceChargeInput,
  SchoolFinanceChargesResult,
  SchoolFinanceFilters,
  SchoolFinanceMutationInput,
  SchoolFinanceOverview,
  SchoolFinanceProfileInput,
  SchoolFinanceProfilesInput,
  SchoolFinanceProfilesResult,
  SchoolFinanceSelection,
  SchoolFinanceStudentCharges,
} from "~/types/schoolFinance";

const base = () => ENDPOINTS.FINANCE.SCHOOL;

class SchoolFinanceModule extends HttpFactory {
  constructor(axios: AxiosInstance) {
    super(axios, "school");
  }

  async getCatalog(signal?: AbortSignal): Promise<SchoolFinanceCatalog> {
    return await this.get(`${base()}catalog/`, {}, { signal });
  }

  async getOverview(
    filters: SchoolFinanceFilters,
    signal?: AbortSignal,
  ): Promise<SchoolFinanceOverview> {
    return await this.get(`${base()}students/`, { ...filters }, { signal });
  }

  async getSelection(
    filters: SchoolFinanceFilters,
    signal?: AbortSignal,
  ): Promise<SchoolFinanceSelection> {
    const { page: _page, pageSize: _pageSize, ...selectionFilters } = filters;
    return await this.get(`${base()}selection/`, selectionFilters, { signal });
  }

  async getStudentCharges(
    studentId: number,
    filters: SchoolFinanceFilters,
    signal?: AbortSignal,
  ): Promise<SchoolFinanceStudentCharges> {
    return await this.get(
      `${base()}students/${studentId}/charges/`,
      {
        year: filters.year,
        month: filters.month,
        status: filters.status,
        category: filters.category,
      },
      { signal },
    );
  }

  async updateProfile(
    studentId: number,
    input: SchoolFinanceProfileInput,
  ): Promise<SchoolFinanceProfilesResult> {
    return await this.patch(`${base()}students/${studentId}/profile/`, input);
  }

  async updateProfiles(
    input: SchoolFinanceProfilesInput,
  ): Promise<SchoolFinanceProfilesResult> {
    return await this.post(`${base()}profiles/bulk/`, input);
  }

  async createCharges(
    input: SchoolFinanceChargeInput,
  ): Promise<SchoolFinanceChargesResult> {
    return await this.post(`${base()}charges/bulk/`, input);
  }

  async mutateCharges(
    action: SchoolFinanceAction,
    input: SchoolFinanceMutationInput,
  ): Promise<SchoolFinanceChargesResult> {
    return await this.post(`${base()}charges/${action}/`, input);
  }
}

export default SchoolFinanceModule;
